import React, { useEffect, useState } from "react";
import api from "../api";
import EmployeeLayout from "./EmployeeLayout";
import {
  User,
  MapPin,
  CreditCard,
  FileBadge,
  Star,
  Briefcase,
  Camera,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function EmployeeProfileTabs() {
  const [profile, setProfile] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get("/employee/me");
      setProfile(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  // PHOTO UPLOAD
 const handlePhotoUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const fd = new FormData();
  fd.append("photo", file);

  try {
    // api instance का इस्तेमाल हो रहा है, baseURL automatically लग जाएगा
    const res = await api.post("/employee/upload-photo", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    setProfile((p) => ({ ...p, photo: res.data.photo }));
    setMsg("success: Profile photo updated!");
  } catch (err) {
    console.error(err);
    setMsg("error: Failed to upload photo");
  }
};

  // PERSONAL INFO UPDATE
  const updatePersonal = async (data) => {
    try {
      await api.put("/employee/update-basic", data);
      setMsg("success: Personal information updated!");
      loadProfile();
    } catch (err) {
      console.error(err);
      setMsg("error: Failed to update personal information");
    }
  };

  // ADDRESS UPDATE
  const updateAddress = async (data) => {
    try {
      await api.put("/employee/update-address", data);
      setMsg("success: Address updated!");
      loadProfile();
    } catch (err) {
      console.error(err);
      setMsg("error: Failed to update address");
    }
  };

  // EMERGENCY UPDATE
  const updateEmergency = async (data) => {
    try {
      await api.put("/employee/update-emergency", data);
      setMsg("success: Emergency contact updated!");
      loadProfile();
    } catch (err) {
      console.error(err);
      setMsg("error: Failed to update emergency contact");
    }
  };

  return (
    <EmployeeLayout>
      <h1 className="text-2xl font-bold mb-6">My Profile</h1>

      {/* Message */}
      {msg && (
        <div
          className={`p-3 mb-4 rounded text-white ${
            msg.startsWith("success") ? "bg-green-600" : "bg-red-600"
          }`}
        >
          {msg.replace("success: ", "").replace("error: ", "")}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-5">
        {/* ---------------- LEFT SIDE TABS ---------------- */}
        <div className="w-full lg:w-64 bg-white shadow border rounded-xl p-4 space-y-1 h-fit order-2 lg:order-1">
          <Tab
            id="overview"
            label="Overview"
            icon={<User size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Tab
            id="personal"
            label="Personal Details"
            icon={<User size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Tab
            id="address"
            label="Address & Emergency Contact"
            icon={<MapPin size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Tab
            id="bank"
            label="Bank Details"
            icon={<CreditCard size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Tab
            id="kyc"
            label="KYC Documents"
            icon={<FileBadge size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Tab
            id="skills"
            label="Skills"
            icon={<Star size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <Tab
            id="experience"
            label="Experience Timeline"
            icon={<Briefcase size={18} />}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>

        {/* ---------------- RIGHT SIDE CONTENT ---------------- */}
        <div className="flex-1 bg-white shadow border rounded-xl p-6 order-1 lg:order-2">
          {activeTab === "overview" && (
            <OverviewTab
              profile={profile}
              onPhotoUpload={handlePhotoUpload}
            />
          )}

          {activeTab === "personal" && (
            <PersonalTab profile={profile} onUpdate={updatePersonal} />
          )}

          {activeTab === "address" && (
            <AddressTab
              profile={profile}
              onUpdateAddress={updateAddress}
              onUpdateEmergency={updateEmergency}
            />
          )}

          {activeTab === "bank" && <BankTab profile={profile} />}
          {activeTab === "kyc" && <KycTab />}
          {activeTab === "skills" && <SkillsTab />}
          {activeTab === "experience" && <ExperienceTab />}
        </div>
      </div>
    </EmployeeLayout>
  );
}

/* ---------------- TABS COMPONENT ---------------- */
function Tab({ id, label, icon, activeTab, setActiveTab }) {
  return (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left ${
        activeTab === id
          ? "bg-green-600 text-white shadow"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      {icon}
      <span className="text-sm lg:text-base">{label}</span>
    </button>
  );
}

/* ---------------- OVERVIEW TAB ---------------- */
function OverviewTab({ profile, onPhotoUpload }) {
  return (
    <>
      <div className="flex flex-col items-center mb-8">
        <div className="relative w-32 h-32 sm:w-36 sm:h-36">
          <img
            src={profile.photo}
            alt="profile"
            className="w-full h-full rounded-full object-cover border shadow bg-gray-100"
            onError={(e) => {
              e.target.src = "/default-user.png";
            }}
          />

          <label className="absolute bottom-2 right-2 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 shadow">
            <Camera size={16} />
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={onPhotoUpload}
            />
          </label>
        </div>

        <h2 className="text-xl font-semibold mt-4 capitalize text-center">
          {profile.first_name} {profile.last_name}
        </h2>

        <p className="text-gray-500 text-sm text-center">
          Employee Code: <b>{profile.employee_code ?? "--"}</b>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Info label="Email" value={profile.email} />
        <Info label="Phone" value={profile.phone} />
        <Info label="Department" value={profile.department_name} />
        <Info label="Designation" value={profile.designation} />
        <Info label="Join Date" value={profile.join_date} />
      </div>
    </>
  );
}

/* ---------------- PERSONAL DETAILS TAB (WITH SAVE) ---------------- */
function PersonalTab({ profile, onUpdate }) {
  const [form, setForm] = useState({
    gender: profile.gender || "",
    dob: profile.dob || "",
    marital_status: profile.marital_status || "",
    blood_group: profile.blood_group || "",
  });

  const submit = (e) => {
    e.preventDefault();
    onUpdate(form);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <h2 className="text-lg font-semibold">Personal Information</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Gender"
          value={form.gender}
          onChange={(e) => setForm({ ...form, gender: e.target.value })}
        />

        <Field
          label="Date of Birth"
          type="date"
          value={form.dob}
          onChange={(e) => setForm({ ...form, dob: e.target.value })}
        />

        <Field
          label="Marital Status"
          value={form.marital_status}
          onChange={(e) =>
            setForm({ ...form, marital_status: e.target.value })
          }
        />

        <Field
          label="Blood Group"
          value={form.blood_group}
          onChange={(e) => setForm({ ...form, blood_group: e.target.value })}
        />
      </div>

      <div className="flex justify-end mt-2">
        <button className="px-4 py-2 bg-green-600 text-white rounded-lg shadow text-sm">
          Save Personal Info
        </button>
      </div>
    </form>
  );
}

/* ---------------- ADDRESS + EMERGENCY TAB (WITH SAVE) ---------------- */
function AddressTab({ profile, onUpdateAddress, onUpdateEmergency }) {
  const [addr, setAddr] = useState({
    address: profile.address || "",
    city: profile.city || "",
    state: profile.state || "",
    pincode: profile.pincode || "",
  });

  const [emg, setEmg] = useState({
    emergency_name: profile.emergency_name || "",
    emergency_phone: profile.emergency_phone || "",
    relationship: profile.relationship || "",
  });

  const submitAddress = (e) => {
    e.preventDefault();
    onUpdateAddress(addr);
  };

  const submitEmergency = (e) => {
    e.preventDefault();
    onUpdateEmergency(emg);
  };

  return (
    <div className="space-y-6">
      {/* Address */}
      <form onSubmit={submitAddress} className="space-y-3">
        <h2 className="text-lg font-semibold">Address Details</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Address"
            value={addr.address}
            onChange={(e) => setAddr({ ...addr, address: e.target.value })}
          />
          <Field
            label="City"
            value={addr.city}
            onChange={(e) => setAddr({ ...addr, city: e.target.value })}
          />
          <Field
            label="State"
            value={addr.state}
            onChange={(e) => setAddr({ ...addr, state: e.target.value })}
          />
          <Field
            label="Pincode"
            value={addr.pincode}
            onChange={(e) => setAddr({ ...addr, pincode: e.target.value })}
          />
        </div>

        <div className="flex justify-end">
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg shadow text-sm">
            Save Address
          </button>
        </div>
      </form>

      {/* Emergency */}
      <form onSubmit={submitEmergency} className="space-y-3 border-t pt-4">
        <h2 className="text-lg font-semibold">Emergency Contact</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Person Name"
            value={emg.emergency_name}
            onChange={(e) =>
              setEmg({ ...emg, emergency_name: e.target.value })
            }
          />
          <Field
            label="Phone Number"
            value={emg.emergency_phone}
            onChange={(e) =>
              setEmg({ ...emg, emergency_phone: e.target.value })
            }
          />
          <Field
            label="Relationship"
            value={emg.relationship}
            onChange={(e) =>
              setEmg({ ...emg, relationship: e.target.value })
            }
          />
        </div>

        <div className="flex justify-end">
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg shadow text-sm">
            Save Emergency Info
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- BANK TAB (VIEW ONLY FOR NOW) ---------------- */
// const Info = ({ label, value }) => (
//   <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
//     <p className="text-sm text-gray-500">{label}</p>
//     <p className="font-medium">{value || "--"}</p>
//   </div>
// );

// Simple Info component
// const Info = ({ label, value }) => (
//   <div className="flex flex-col">
//     <span className="text-gray-500 text-sm">{label}</span>
//     <span className="text-gray-800 font-medium">{value || "-"}</span>
//   </div>
// );
function BankTab() {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBank = async () => {
      try {
        const res = await api.get("/banks/me"); // <-- match backend
        setBank(res.data);
      } catch (err) {
        console.error("Bank fetch error", err);
        setBank(null);
      } finally {
        setLoading(false);
      }
    };

    loadBank();
  }, []);

  if (loading) return <p className="text-gray-500">Loading bank details...</p>;
  if (!bank) return <p className="text-gray-500">Bank details not added yet.</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Bank Information</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Info label="Account Holder" value={bank.account_holder} />
        <Info label="Bank Name" value={bank.bank_name} />
        <Info label="Account Number" value={bank.account_no} />
        <Info label="IFSC Code" value={bank.ifsc} />
        <Info label="UPI ID" value={bank.upi_id} />
        <Info label="Branch Name" value={bank.branch_name} />
      </div>
    </div>
  );
}

/* ---------------- KYC TAB (DUMMY) ---------------- */
/* ---------------- KYC TAB ---------------- */
const DOC_TYPES = ["Aadhar", "PAN", "Driving License", "Passport", "Bank Passbook", "Photo", "Other"];

function KycTab() {
  const [activeTab, setActiveTab] = useState("upload");
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load employee documents
  const loadDocuments = async () => {
    try {
      setLoading(true);
      const res = await api.get("/employee/upload/kyc");
      setDocuments(res.data || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // Upload document
  const handleUpload = async () => {
    if (!file) return alert("Please select a file");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);

    try {
     await api.post("/employee/upload/kyc", formData, {
  headers: { "Content-Type": "multipart/form-data" },
});

      setFile(null);
      loadDocuments();
      alert("Document uploaded successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to upload document");
    }
  };

  return (
    <div className="p-5">
      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          className={`px-4 py-2 ${activeTab === "upload" ? "border-b-2 border-indigo-600 font-bold" : ""}`}
          onClick={() => setActiveTab("upload")}
        >
          Upload Document
        </button>
        <button
          className={`px-4 py-2 ${activeTab === "status" ? "border-b-2 border-indigo-600 font-bold" : ""}`}
          onClick={() => setActiveTab("status")}
        >
          My Documents
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "upload" && (
        <div className="p-4 border rounded-lg bg-white">
          <h2 className="font-bold mb-3">Upload Document</h2>
          <div className="flex gap-3 items-center">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="border rounded px-3 py-2"
            >
              {DOC_TYPES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <input type="file" onChange={(e) => setFile(e.target.files[0])} />

            <button
              onClick={handleUpload}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
            >
              Upload
            </button>
          </div>
        </div>
      )}

      {activeTab === "status" && (
        <div className="p-4 border rounded-lg bg-white overflow-x-auto">
          <h2 className="font-bold mb-3">My Documents</h2>
          {loading ? (
            <p>Loading...</p>
          ) : documents.length === 0 ? (
            <p className="text-slate-500">No documents uploaded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left">Document Type</th>
                  <th className="px-4 py-2 text-left">File</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Remark</th>
                  <th className="px-4 py-2 text-left">Uploaded At</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{doc.doc_type}</td>
                    <td className="px-4 py-2">
                      <a
                        href={`${process.env.REACT_APP_API_URL}/${doc.file_url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 underline"
                      >
                        View
                      </a>
                    </td>
                    <td className="px-4 py-2 font-semibold">{doc.status || "pending"}</td>
                    <td className="px-4 py-2">{doc.remark || "-"}</td>
                    <td className="px-4 py-2">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// Upload component
function Upload({ label, onChange }) {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border p-3 rounded-lg">
      <span>{label}</span>
      <input type="file" className="text-sm" onChange={onChange} />
    </div>
  );
}

/* ---------------- SKILLS TAB (STATIC CHIPS) ---------------- */




function SkillTag({ skill }) {
  return (
    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
      {skill.label}
    </span>
  );
}

function SkillsTab() {
  const [skills, setSkills] = useState([]);
  const [studentId, setStudentId] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")); // or wherever you store
    if (user && user.id) {
      setStudentId(user.id);
    }
  }, []);

  // Load skills for logged-in student
  useEffect(() => {
    if (!studentId) return;

    const loadSkills = async () => {
      try {
        const res = await api.get(`/skills?employee_id=${studentId}`);
        setSkills(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error loading skills:", err);
        setSkills([]);
      }
    };

    loadSkills();
  }, [studentId]);

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">My Skills</h2>

      <div className="flex flex-wrap gap-2">
        {skills.length === 0 ? (
          <p className="text-gray-500">No skills available</p>
        ) : (
          skills.map((skill) => <SkillTag key={skill.id} skill={skill} />)
        )}
      </div>
    </div>
  );
}
/* ---------------- EXPERIENCE TAB (STATIC TIMELINE) ---------------- */
function ExperienceTab() {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExperiences();
  }, []);

 const loadExperiences = async () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user?.id) {
      console.error("Employee not logged in");
      return;
    }

    const res = await api.get(
      `/experiences?employee_id=${user.id}`
    );

    setExperiences(res.data || []);
  } catch (err) {
    console.error("Experience load error", err);
    setExperiences([]);
  } finally {
    setLoading(false);
  }
};


  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">
        Experience Timeline
      </h2>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : experiences.length === 0 ? (
        <p className="text-gray-500">No experience added yet</p>
      ) : (
        <div className="space-y-8 border-l-2 border-green-500 pl-6 relative">
          {experiences.map((exp) => (
            <ExpItem
              key={exp.id}
              year={new Date(exp.date_from).getFullYear()}
              role={exp.title}
              company={exp.company_name || "Company"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- REUSABLE COMPONENTS ---------------- */

function Info({ label, value }) {
  return (
    <div className="p-3 border rounded-lg bg-gray-50 shadow-sm">
      <p className="text-gray-600 text-sm">{label}</p>
      <p className="font-semibold">{value || "--"}</p>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block mb-1 text-sm font-medium">{label}</label>
      <input
        className="w-full border p-2 rounded-lg text-sm focus:ring-2 focus:ring-green-400 outline-none"
        {...props}
      />
    </div>
  );
}



function ExpItem({ year, role, company }) {
  return (
    <div className="relative -left-7">
      <div className="absolute left-0 top-2 w-3 h-3 bg-green-600 rounded-full -translate-x-1/2"></div>
      <div className="bg-white p-4 rounded-lg shadow border">
        <p className="font-semibold text-lg">{year}</p>
        <p className="text-gray-700">
          {role} - {company}
        </p>
      </div>
    </div>
  );
}